import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import ModalContainer from '../../../components/ModalContainer'
import VolumeStatsModalContent from '../../../components/VolumeStatsModalContent'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import { type FC, useMemo, useState } from 'react'
import { type TMutation } from '../../../schema/mutation'
import { type TOwner } from '../../../schema/owner'
import { type TMetaTitle } from '../../../schema/metaTitle'
import { useTranslation } from 'react-i18next'
import { useLanguageCode } from '../../../hooks/useLanguageCode'
import { type TVolumeDetail } from '../../../schema/volume'
import { Link, useSearchParams } from 'react-router-dom'
import { validate as uuidValidate } from 'uuid'
import { BACK_META_TITLE_ID } from '../../../utils/constants'
import CollapsableSidebar from '../../../components/CollapsableSidebar'
import theme from '../../../theme'
import { getMutationMarkLabel } from '../../../utils/mutationMark'
import { useFormatDate } from '../../../utils/date'

interface InputDataProps {
  volume: TVolumeDetail
  volumeId: string | undefined
  mutations: TMutation[]
  owners: TOwner[]
  metaTitles: TMetaTitle[]
}

const InputData: FC<InputDataProps> = ({
  volume,
  volumeId,
  mutations,
  owners,
  metaTitles,
}) => {
  const { t, i18n } = useTranslation()
  const { formatDate } = useFormatDate()
  const { languageCode } = useLanguageCode()

  const [searchParams] = useSearchParams()
  const backMetaTitleId = searchParams.get(BACK_META_TITLE_ID)

  const backMetaTitle = useMemo(
    () =>
      uuidValidate(backMetaTitleId || '')
        ? backMetaTitleId
        : volume?.volume.metaTitleId,
    [backMetaTitleId, volume.volume.metaTitleId]
  )

  const [modalOpened, setModalOpened] = useState(false)

  return (
    <CollapsableSidebar>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          // boxShadow: theme.shadows[1],
          justifyContent: 'space-between',
          flexShrink: 0,
          height: '100%',
        }}
      >
        <Typography
          sx={{
            marginBottom: '8px',
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            fontSize: '24px',
          }}
        >
          {t('volume_overview.volume_information')}
        </Typography>

        <Box
          sx={{
            flexGrow: 1,
            flexShrink: 1,
            overflowY: 'auto',
            marginTop: '8px',
          }}
        >
          <Table
            size="small"
            sx={{ overflowY: 'auto', flexShrink: 1, flexGrow: 1 }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {t('common.fields.name')}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {t('volume_overview.value')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{t('common.fields.meta_title')}</TableCell>
                <TableCell>
                  {
                    metaTitles.find((m) => m.id === volume.volume.metaTitleId)
                      ?.name
                  }
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('common.fields.sub_name')}</TableCell>
                <TableCell>{volume.volume.subName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('common.fields.mutation')}</TableCell>
                <TableCell>
                  {
                    mutations.find((m) => m.id === volume.volume.mutationId)
                      ?.name[languageCode]
                  }
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('common.fields.mutation_mark')}</TableCell>
                <TableCell>
                  {getMutationMarkLabel(volume.volume.mutationMark)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('common.fields.bar_code')}</TableCell>
                <TableCell>{volume.volume.barCode}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('common.fields.signature')}</TableCell>
                <TableCell>{volume.volume.signature}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('volume_overview.year')}</TableCell>
                <TableCell>{volume.volume.year}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('volume_overview.date_from')}</TableCell>
                <TableCell>{formatDate(volume.volume.dateFrom)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('volume_overview.first_number')}</TableCell>
                <TableCell>{volume.volume.firstNumber}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('volume_overview.date_to')}</TableCell>
                <TableCell>{formatDate(volume.volume.dateTo)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('volume_overview.last_number')}</TableCell>
                <TableCell>{volume.volume.lastNumber}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('common.fields.owner')}</TableCell>
                <TableCell>
                  {
                    owners.find((o) => o.id === volume.volume.ownerId)
                      ?.shorthand
                  }
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('volume_overview.note')}</TableCell>
                <TableCell>{volume.volume.note}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        <Button
          sx={{
            marginTop: '10px',
            marginBottom: '10px',
            flexShrink: 0,
            flexGrow: 0,
          }}
          variant="contained"
          onClick={() => setModalOpened(true)}
        >
          {t('specimens_overview.volume_overview_modal_link')}
        </Button>
        {backMetaTitle && (
          <Button
            component={Link}
            sx={{ flexShrink: 0, flexGrow: 0 }}
            variant="outlined"
            to={`/${i18n.resolvedLanguage}/${t('urls.specimens_overview')}/${backMetaTitle}`}
          >
            {t('volume_overview.back_to_specimens_overview')}
          </Button>
        )}
        <ModalContainer
          autoWidth
          minWidth="30rem"
          onClose={() => setModalOpened(false)}
          header={t('specimens_overview.volume_overview_modal_link')}
          opened={modalOpened}
          closeButton={{
            callback: () => setModalOpened(false),
          }}
        >
          <VolumeStatsModalContent volumeId={volumeId} />
        </ModalContainer>
      </Box>
    </CollapsableSidebar>
  )
}

export default InputData
